

export interface Message {
    role: 'user' | 'magnus';
    content: string;
    action?: {
        type: 'navigate';
        target: string;
        label?: string;
    };
    tooltip?: string;
}

export interface PageContext {
    name: string;
    purpose: string;
    nextSteps: string[];
    relatedPages: Array<{ path: string; label: string; why: string }>;
    features: string[];
}

export const MAGNUS_AVATAR = '🤖';
export const MAGNUS_EMOJI_VARIANTS = {
    thinking: '🤔',
    happy: '😊',
    analyzing: '🔍',
    success: '✅',
    warning: '⚠️',
    resolve: '🔧',
};

export const PAGE_CONTEXT: Record<string, PageContext> = {
    '/': {
        name: 'Magnus Resolve Dashboard',
        purpose: 'Monitor FRACAS incidents, 8D reports, and reliability metrics at a glance',
        nextSteps: ['Review open incidents', 'Check overdue 8D reports', 'Run Weibull analysis', 'Review warranty costs'],
        relatedPages: [
            { path: '/fracas', label: 'FRACAS', why: 'Manage all failure incidents' },
            { path: '/8d', label: '8D Reports', why: 'Problem-solving workflows' },
            { path: '/analytics', label: 'Analytics', why: 'Advanced reliability tools' },
        ],
        features: ['FRACAS tracking', '8D management', 'RCA tools', 'Analytics'],
    },
    '/fracas': {
        name: 'FRACAS Incident Tracking',
        purpose: 'Failure Reporting, Analysis, and Corrective Action System - track all product failures',
        nextSteps: ['Create new incident', 'Assign root cause analysis', 'Link to 8D report', 'Run 5-Why'],
        relatedPages: [
            { path: '/rca/5why', label: '5-Why Analysis', why: 'Find root causes' },
            { path: '/rca/ishikawa', label: 'Ishikawa Diagram', why: 'Visualize causes' },
            { path: '/8d', label: '8D Reports', why: 'Structured problem solving' },
        ],
        features: ['Incident logging', 'Status tracking', 'Severity classification', 'RCA linkage'],
    },
    '/8d': {
        name: '8D Problem Solving',
        purpose: '8 Disciplines methodology for structured root cause analysis and corrective action',
        nextSteps: ['Create 8D report', 'Complete D0-D8 steps', 'Link to FRACAS', 'Track containment actions'],
        relatedPages: [
            { path: '/fracas', label: 'FRACAS', why: 'Link to incidents' },
            { path: '/rca', label: 'RCA Tools', why: 'Use 5-Why and Ishikawa' },
        ],
        features: ['D0-D8 workflow', 'Team formation', 'Root cause analysis', 'Corrective actions'],
    },
    '/rca': {
        name: 'RCA Tools Hub',
        purpose: 'Root Cause Analysis tools including 5-Why, Ishikawa (Fishbone), and FTA',
        nextSteps: ['Select RCA tool', 'Link to incident', 'Perform analysis', 'Document findings'],
        relatedPages: [
            { path: '/fracas', label: 'FRACAS', why: 'Link to incidents' },
            { path: '/analytics/fta', label: 'Fault Tree Analysis', why: 'System-level analysis' },
        ],
        features: ['5-Why', 'Ishikawa Diagram', 'FTA integration'],
    },
    '/analytics': {
        name: 'Analytics Dashboard',
        purpose: 'Reliability metrics and trends visualization',
        nextSteps: ['Run Weibull analysis', 'Calculate MTBF/MTTR', 'Review warranty forecasts', 'Build fault tree'],
        relatedPages: [
            { path: '/analytics/weibull', label: 'Weibull Analysis', why: 'Life predictions' },
            { path: '/analytics/mtbf', label: 'MTBF/MTTR', why: 'Availability tracking' },
            { path: '/analytics/warranty', label: 'Warranty', why: 'Cost forecasting' },
            { path: '/analytics/fta', label: 'FTA Editor', why: 'System reliability' },
        ],
        features: ['Weibull', 'MTBF/MTTR', 'Warranty tracking', 'FTA', 'Reports'],
    },
    '/analytics/weibull': {
        name: 'Weibull Analysis',
        purpose: 'Calculate B10/B50 life predictions and reliability parameters',
        nextSteps: ['Enter failure data', 'Calculate parameters', 'Review B10 life', 'Schedule maintenance'],
        relatedPages: [
            { path: '/analytics/mtbf', label: 'MTBF Calculator', why: 'System reliability' },
            { path: '/fracas', label: 'FRACAS', why: 'Use failure data' },
        ],
        features: ['Beta/Eta calculation', 'B10/B50/B90 life', 'Confidence intervals', 'PM recommendations'],
    },
    '/analytics/mtbf': {
        name: 'MTBF/MTTR Calculator',
        purpose: 'Track Mean Time Between Failures and availability metrics',
        nextSteps: ['Log failure events', 'Calculate MTBF', 'Review availability', 'Identify top failure modes'],
        relatedPages: [
            { path: '/analytics/weibull', label: 'Weibull', why: 'Life prediction' },
            { path: '/fracas', label: 'FRACAS', why: 'Pull failure data' },
        ],
        features: ['MTBF calculation', 'MTTR tracking', 'Availability %', 'Failure distribution'],
    },
    '/analytics/warranty': {
        name: 'Warranty Tracking',
        purpose: 'Manage warranty claims and forecast future costs',
        nextSteps: ['Add claims', 'Review forecast', 'Track trends', 'Budget planning'],
        relatedPages: [
            { path: '/fracas', label: 'FRACAS', why: 'Link warranty failures' },
            { path: '/analytics', label: 'Analytics', why: 'View all metrics' },
        ],
        features: ['Claim tracking', '6-month forecast', 'Cost analysis', 'Trend detection'],
    },
    '/analytics/fta': {
        name: 'Fault Tree Analysis',
        purpose: 'Visual system reliability analysis with AND/OR gates',
        nextSteps: ['Build fault tree', 'Add gates and events', 'Set probabilities', 'Calculate top event'],
        relatedPages: [
            { path: '/rca', label: 'RCA Tools', why: 'Use other RCA methods' },
            { path: '/fracas', label: 'FRACAS', why: 'Analyze critical failures' },
        ],
        features: ['Tree builder', 'Gate logic', 'Probability calc', 'Importance measures'],
    },
    '/rca/5why': {
        name: '5-Why Analysis',
        purpose: 'Iterative questioning technique to find root causes',
        nextSteps: ['Ask "Why?"', 'Drill down 5 levels', 'Find root cause', 'Define controls'],
        relatedPages: [
            { path: '/rca/ishikawa', label: 'Ishikawa', why: 'Alternative RCA method' },
            { path: '/fracas', label: 'FRACAS', why: 'Link to incident' },
        ],
        features: ['Iterative questioning', 'Root cause identification', 'Control definition'],
    },
    '/rca/ishikawa': {
        name: 'Ishikawa Diagram',
        purpose: 'Fishbone diagram to visualize cause categories (6M, 4M, etc.)',
        nextSteps: ['Define problem', 'Add cause categories', 'Brainstorm causes', 'Prioritize'],
        relatedPages: [
            { path: '/rca/5why', label: '5-Why', why: 'Drill deeper' },
            { path: '/analytics/fta', label: 'FTA', why: 'System analysis' },
        ],
        features: ['Fishbone visualization', '6M/4M categories', 'Cause brainstorming'],
    },
};

export const RESOLVE_KNOWLEDGE: Record<string, string> = {
    'fracas': 'FRACAS = Failure Reporting, Analysis, and Corrective Action System. Track all failures systematically.',
    'weibull': 'Weibull analysis predicts B10 life (when 10% fail) using Beta (shape) and Eta (scale) parameters.',
    'mtbf': 'MTBF = Mean Time Between Failures. MTTR = Mean Time To Repair. Availability = MTBF/(MTBF+MTTR) * 100%.',
    'warranty': 'Track warranty claims and use historical data to forecast future costs with 90%+ confidence.',
    'fta': 'Fault Tree Analysis uses AND/OR gates to calculate system-level failure probability from component failures.',
    '5why': 'Ask "Why?" 5 times iteratively to drill down from symptoms to root cause.',
    'ishikawa': 'Fishbone diagram with 6M causes: Man, Machine, Material, Method, Measurement, Environment.',
    '8d': '8 Disciplines: D0-Team, D1-Problem, D2-Contain, D3-Root Cause, D4-Fix, D5-Validate, D6-Prevent, D7-Recognize, D8-Close.',
    'rca': 'Root Cause Analysis - Find true underlying causes, not just symptoms. Use 5-Why, Ishikawa, or FTA.',
    'b10': 'B10 Life = Time when 10% of population fails. Use for PM scheduling (typically at 80% of B10).',
    'beta': 'Beta (β) = Weibull shape parameter. β<1: infant mortality, β=1: random, β>1: wear-out, β>3: rapid wear-out.',
};
